import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'
import { Sequence, sequenceByCampaignKey } from 'services/sequences'

// Types

export interface SequenceTemplateStep {
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
  is_variant: boolean
  thread_reply: boolean
  // Present on variants only: the parent step's order within the snapshot.
  variant_from_step?: number
}

/** Lightweight list row (no full step bodies). */
export interface SequenceTemplateListItem {
  id: number
  name: string
  description: string
  step_count: number
  created_at: string
  updated_at: string
}

/** Full template, including the step snapshot. */
export interface SequenceTemplateDetail {
  id: number
  name: string
  description: string
  steps: SequenceTemplateStep[]
  created_at: string
  updated_at: string
}

export interface SaveSequenceTemplatePayload {
  campaign_id: number
  name: string
  description?: string
}

// Query keys
export const sequenceTemplateKeys = {
  all: ['sequence-templates'] as const,
  list: () => [...sequenceTemplateKeys.all, 'list'] as const,
  detail: (id: number) => [...sequenceTemplateKeys.all, 'detail', id] as const,
}

// API calls
const listSequenceTemplates = async (): Promise<SequenceTemplateListItem[]> => {
  // Read query — fail quietly (no error toast); the drawer renders an
  // empty/loading state instead. Avoids a scary toast if the endpoint isn't
  // available yet or returns nothing.
  return baseAPICall<SequenceTemplateListItem[]>(
    () => clientInstance.get('/emailbison/sequence-templates/'),
    { silentStatuses: [404] }
  )
}

const getSequenceTemplate = async (
  id: number
): Promise<SequenceTemplateDetail> => {
  return baseAPICall<SequenceTemplateDetail>(
    () => clientInstance.get(`/emailbison/sequence-templates/${id}/`),
    { silentStatuses: [404] }
  )
}

const saveSequenceTemplate = async (
  payload: SaveSequenceTemplatePayload
): Promise<SequenceTemplateDetail> => {
  return baseAPICall<SequenceTemplateDetail>(() =>
    clientInstance.post('/emailbison/sequence-templates/', payload)
  )
}

const deleteSequenceTemplate = async (id: number): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(`/emailbison/sequence-templates/${id}/`)
  )
}

const applySequenceTemplate = async (
  templateId: number,
  campaignId: number
): Promise<Sequence> => {
  return baseAPICall<Sequence>(() =>
    clientInstance.post(`/emailbison/sequence-templates/${templateId}/apply/`, {
      campaign_id: campaignId,
    })
  )
}

// Hooks
export const useSequenceTemplates = (enabled = true) => {
  return useQuery({
    queryKey: sequenceTemplateKeys.list(),
    queryFn: listSequenceTemplates,
    enabled,
  })
}

export const useSequenceTemplate = (id: number | null) => {
  return useQuery({
    queryKey: sequenceTemplateKeys.detail(id ?? 0),
    queryFn: () => getSequenceTemplate(id as number),
    enabled: id != null && id > 0,
  })
}

export const useSaveSequenceAsTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: saveSequenceTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequenceTemplateKeys.list() })
    },
  })
}

export const useDeleteSequenceTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteSequenceTemplate,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: sequenceTemplateKeys.list() })
      // Drop the now-stale detail entry so a recreated id can't read a ghost.
      queryClient.removeQueries({ queryKey: sequenceTemplateKeys.detail(id) })
    },
  })
}

/** Applying a template rewrites the campaign's sequence, so invalidate it. */
export const useApplySequenceTemplate = (campaignId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (templateId: number) =>
      applySequenceTemplate(templateId, campaignId),
    onSuccess: (data) => {
      queryClient.setQueryData(sequenceByCampaignKey(campaignId), data)
      queryClient.invalidateQueries({
        queryKey: sequenceByCampaignKey(campaignId),
      })
    },
  })
}
